import {
	ArrowLeftIcon,
	ArrowLeftToLineIcon,
	ArrowRightIcon,
	ArrowRightToLineIcon,
	Loader2Icon,
	RotateCcwIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "./button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	FWQUERYBROWSER_DEFAULT_PAGESIZE,
	type FwQueryState,
} from "./useFwQuery";
import { useTailwindMediaQueries } from "@/hooks/useTailwindMediaQueries";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface FwQueryPageProps<T> {
	query: FwQueryState<T>;
}

export function FwQueryPager<T>({ query }: FwQueryPageProps<T>) {
	const [pageNo, setPageNo] = useState<number>(query.pageNo);
	//const isMobile = useIsMobile()
	const mediaQueries = useTailwindMediaQueries();
	const isRefreshing = query.isPending || query.isFetching;

	// Update local pageNo when query.pageNo changes
	useEffect(() => {
		// Only update if values don't match to prevent unnecessary re-renders
		if (pageNo !== query.pageNo) {
			setPageNo(query.pageNo);
		}
	}, [query.pageNo, pageNo]);

	// Memoize handlers to prevent them from being recreated on each render
	const handleFirstPage = useCallback(() => {
		return query.firstPageAsync();
	}, [query]);

	const handlePrevPage = useCallback(() => {
		return query.prevPageAsync();
	}, [query]);

	const handleNextPage = useCallback(() => {
		return query.nextPageAsync();
	}, [query]);

	const handleLastPage = useCallback(() => {
		return query.lastPageAsync();
	}, [query]);

	const handleRefresh = useCallback(() => {
		return query.loadAsync();
	}, [query]);

	const handlePageNoChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newPageNo = parseInt(e.target.value, 10) || 1;
			setPageNo(newPageNo);
		},
		[],
	);

	const handlePageNoBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			const newPageNo = parseInt(e.target.value, 10) || 1;
			return query.setPageNoAsync(newPageNo);
		},
		[query],
	);

	const handlePageSizeChange = useCallback(
		(value: string | null) => {
			return query.setPageSizeAsync(
				parseInt(value ?? FWQUERYBROWSER_DEFAULT_PAGESIZE.toString(), 10) ||
					FWQUERYBROWSER_DEFAULT_PAGESIZE,
			);
		},
		[query],
	);

	// Memoize page stats to prevent unnecessary calculations
	const pageStats = useMemo(() => {
		const start = query.pageNo * query.pageSize - query.pageSize + 1;
		const end = Math.min(query.items.length * query.pageNo, query.totalItems);
		return `${start} - ${end} of ${query.totalItems} records`;
	}, [query.pageNo, query.pageSize, query.items.length, query.totalItems]);

	const refreshIndicator = (
		<Button
			variant="outline"
			size="sm"
			title="Refresh"
			aria-label="Refresh data"
			disabled={isRefreshing}
			onClick={handleRefresh}
			className="shrink-0"
			aria-busy={isRefreshing}
		>
			{isRefreshing ? (
				<>
					<Loader2Icon className="h-4 w-4 animate-spin" />
					<span className="sr-only">Refreshing...</span>
				</>
			) : (
				<RotateCcwIcon className="h-4 w-4" />
			)}
		</Button>
	);

	const paginationControls = (
		<div className="flex gap-2 justify-center items-center">
			<Button
				variant="default"
				size="sm"
				title="First"
				disabled={query.firstBtnDisabled}
				onClick={handleFirstPage}
			>
				<ArrowLeftToLineIcon />
			</Button>
			<Button
				variant="default"
				size="sm"
				title="Previous"
				disabled={query.prevBtnDisabled}
				onClick={handlePrevPage}
			>
				<ArrowLeftIcon />
			</Button>
			{mediaQueries.desktop && (
				<div className="text-sm pl-1 pr-1 text-muted-foreground flex items-center truncate">
					<Label htmlFor="page-number">Page No:</Label>
				</div>
			)}
			<div>
				<Input
					name="page-number"
					placeholder="Page No"
					className="w-[60px] text-center"
					value={pageNo}
					type="number"
					min={query.totalPages === 0 ? 0 : 1}
					max={query.totalPages}
					disabled={query.pageNoBtnDisabled}
					onChange={handlePageNoChange}
					onBlur={handlePageNoBlur}
				/>
			</div>
			<div className="text-sm pl-1 pr-1 text-muted-foreground flex items-center truncate">
				of {query.totalPages}
			</div>
			<Button
				variant="default"
				size="sm"
				title="Next"
				disabled={query.nextBtnDisabled}
				onClick={handleNextPage}
			>
				<ArrowRightIcon />
			</Button>
			<Button
				variant="default"
				size="sm"
				title="Last"
				disabled={query.lastBtnDisabled}
				onClick={handleLastPage}
			>
				<ArrowRightToLineIcon />
			</Button>
			{refreshIndicator}
		</div>
	);

	const pageSizeControls = (
		<div className="flex justify-end items-center">
			<div className="text-sm pl-4 pr-1 text-muted-foreground flex items-center truncate">
				Page Size:
			</div>
			<Select
				value={query.pageSize.toString()}
				disabled={query.pageSizeBtnDisabled}
				onValueChange={handlePageSizeChange}
				name="Records per Page"
			>
				<SelectTrigger className="w-[90px]">
					<SelectValue placeholder="Records per Page" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="5">5</SelectItem>
					<SelectItem value="10">10</SelectItem>
					<SelectItem value="15">15</SelectItem>
					<SelectItem value="20">20</SelectItem>
					<SelectItem value="25">25</SelectItem>
					<SelectItem value="50">50</SelectItem>
					<SelectItem value="100">100</SelectItem>
					<SelectItem value="250">250</SelectItem>
					<SelectItem value="500">500</SelectItem>
					<SelectItem value="1000">1000</SelectItem>
				</SelectContent>
			</Select>
		</div>
	);

	return (
		<div className="sticky -bottom-px z-50 bg-background pb-2">
			{mediaQueries.phone ? (
				<div className="flex flex-col gap-4">
					{paginationControls}
					<div className="flex justify-between items-center">
						<div className="flex items-center gap-2">
							<p className="text-sm text-muted-foreground truncate">
								{pageStats}
							</p>
						</div>
						{pageSizeControls}
					</div>
				</div>
			) : (
				<div className="flex justify-between items-center">
					<div className="flex flex-1 items-center gap-2">
						<p className="text-sm text-muted-foreground">{pageStats}</p>
					</div>
					<div className="flex-2">{paginationControls}</div>
					<div className="flex-1">{pageSizeControls}</div>
				</div>
			)}
		</div>
	);
}
